const booleanStrings = {
  true: true,
  yes: true,
  on: true,
  false: false,
  no: false,
  off: false,
  '1': true,
  '0': false,
};

export function coerceBoolean(value: boolean | string | number, defaultValue: boolean = false): boolean {
  if (value === 'default' || value == null) {
    return defaultValue;
  } else if (typeof value === 'string') {
    const lookup = booleanStrings[value];
    if (typeof lookup !== 'boolean') {
      throw new Error(`Cannot convert "${value}" to a boolean.`);
    }
    return lookup;
  } else if (typeof value === 'number') {
    return !!value;
  } else {
    return value;
  }
}

export function coerceNumber(value: string | number, defaultValue?: number): number {
  if (typeof value === 'number') {
    return value;
  } else if (value === 'default' || value == null) {
    return defaultValue;
  } else if (typeof value === 'string') {
    const converted = +value;
    if (isNaN(converted)) {
      throw new Error(`Cannot convert "${value}" to a number.`);
    }
    return converted;
  } else {
    throw new Error(`Cannot convert "${JSON.stringify(value)}" to a number.`);
  }
}

export function defaulted<T>(...values: T[]);
export function defaulted() {
  for (let i = 0; i < arguments.length; i++) {
    const value = arguments[i];
    if (value !== undefined) {
      return value;
    }
  }
  return undefined;
}

export function firstNotNull<T>(...values: T[]): T;
export function firstNotNull<T>(arg1: T, arg2: NonNullable<T>): NonNullable<T>;
export function firstNotNull<T>(arg1: T, arg2: T, arg3: NonNullable<T>): NonNullable<T>;
export function firstNotNull<T>(arg1: T, arg2: T, arg3: T, arg4: NonNullable<T>): NonNullable<T>;
export function firstNotNull() {
  for (let i = 0; i < arguments.length; i++) {
    const value = arguments[i];
    if (value != null) {
      return value;
    }
  }
  return undefined;
}

/**
 * Strip 4-byte UTF characters, as database (yet) can only handle 3-byte UTF!
 *
 * @param text Any text, that can contain 4-byte UTF characters.
 */
export function stripManyFourByteChars(text: string): string {
  // https://stackoverflow.com/questions/38895537/how-to-remove-utf16-characters-from-string
  // "... it does not covers all the utf-16 characters, but removes many of them"
  // https://stackoverflow.com/questions/8491431/how-to-replace-remove-4-byte-characters-from-a-utf-8-string-in-php
  // "... you should not just strip, but replace with replacement character U+FFFD to avoid unicode attacks, mostly XSS"
  // http://unicode.org/reports/tr36/#Deletion_of_Noncharacters
  if (text && (typeof text === 'string')) {
    return text.replace(/([\uE000-\uF8FF]|\uD83C[\uDF00-\uDFFF]|\uD83D[\uDC00-\uDDFF])/g, '?'); // '\uFFFD');
  }
  return text;
}

/**
 * Checks whether the specified object is an empty object (i.e., {}).
 *
 * @param x The object to check
 */
export function isEmptyObject(x: any): boolean {
  if (typeof x !== 'object') {
    return false;
  } else if (x == null) {
    return true;
  }

  return Object.keys(x).length === 0;
}

// flattens an array of arrays
export function flatten<T>(arr: T[][]): T[] {
  if (!arr || !arr.length) {
    return [];
  }

  return arr.reduce((agg, curr) => {
    agg.push(...curr);
    return agg;
  }, []);
}

/**
 * Returns an array copy with only distinct elements
 *
 * @param array
 */
export function unique<T>(array: ReadonlyArray<T>): Array<T> {
  return array.filter((value, index, arr) => arr.indexOf(value) === index);
}

/**
 * Represents a resource or service that can be cleaned up. Also used for wrapping
 * subscriptions and (temporary) registrations.
 */
export type DisposableFn = Function;

/*
 * Returns an object where all (top-level) properties that are `undefined` are removed,
 * or the original object if there are no top-level `undefined` properties.
 *
 * @param obj The object to filter.
 */
export function filterUndefinedProps<T>(
  obj: T
): T {
  if (!obj) {
    return obj;
  }

  let result: T;

  for (const propName in obj) {
    if (obj.hasOwnProperty(propName)) {
      const curData = obj[propName] as any;
      if (curData === undefined) {
        if (!result) {
          result = { ...obj };
        }
        delete result[propName];
      }
    }
  }

  return result || obj;
}

/**
 * Changes the timezone of the date to UTC without messing up the date/month/year (resets time to midnight)
 *
 * @param nonUtc previous date in non-utc form, e.g. 2017-01-01 00:00 GMT+2 (would be 2016-12-31 22:00 in UTC)
 * @returns date with the same day, month and year, but in UTC, e.g. 2017-01-01 00:00 GMT+0
 */
export function dateToUTC(nonUtc: Date): Date {
  const tmstmp = Date.UTC(nonUtc.getFullYear(), nonUtc.getMonth(), nonUtc.getDate());
  return new Date(tmstmp);
}

/**
 * Changes the timezone of the date to the local timezone without messing up the date/month/year (resets time to midnight)
 *
 * @param nonLocal the original date about to be converted to local
 * @returns date with the same day, month and year, but in local timezone
 */
export function dateToLocal(nonLocal: Date): Date {
  return new Date(nonLocal.getFullYear(), nonLocal.getMonth(), nonLocal.getDate());
}
